import React, { useEffect, useRef } from "react";
import { colors } from "../theme/colors";

/**
 * マイク音量に反応する波形バー（thread-ui-design の voice-waveform を移植）。
 * - 常時ゆるやかに動くアイドルアニメーション
 * - active=true でマイクを開き、音量に応じてバーが伸び、色がアイドル→primary(青)へ
 * - マイク不許可でもアイドル波形は動き続ける
 */
interface VoiceWaveformProps {
  active: boolean;
  barCount?: number;
  width?: number;
  height?: number;
}

// "#RRGGBB" → [r,g,b]
const hexToRgb = (hex: string): [number, number, number] => {
  const v = hex.replace("#", "");
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
};

const IDLE_RGB = hexToRgb(colors.text.secondary); // グレー
const PRIMARY_RGB = hexToRgb(colors.primary.main); // 青

const mixColor = (t: number): string => {
  const r = Math.round(IDLE_RGB[0] + (PRIMARY_RGB[0] - IDLE_RGB[0]) * t);
  const g = Math.round(IDLE_RGB[1] + (PRIMARY_RGB[1] - IDLE_RGB[1]) * t);
  const b = Math.round(IDLE_RGB[2] + (PRIMARY_RGB[2] - IDLE_RGB[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

export const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  active,
  barCount = 13,
  width = 120,
  height = 28,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  // スクロール用の履歴バッファ。index0 が最新(左端)、末尾が最古(右端)。
  // 左端に新しいバーを差し込み、時間とともに右へ流していく（左→右）。
  const levelsRef = useRef<number[]>(new Array(barCount + 2).fill(0));
  const phaseRef = useRef(0); // 次のシフトまでの進捗(0-1)。滑らかな横移動用。
  const lastRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const startMic = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);
        analyserRef.current = analyser;
        dataRef.current = new Uint8Array(analyser.frequencyBinCount);
      } catch {
        // マイク不許可でもアイドル波形は動き続ける
        analyserRef.current = null;
      }
    };

    const stopMic = () => {
      analyserRef.current = null;
      dataRef.current = null;
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    if (active) {
      void startMic();
    } else {
      stopMic();
    }

    return () => {
      cancelled = true;
      stopMic();
    };
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // 1バー分だけ進むのにかける秒数（小さいほど速く流れる）。
    const STEP_INTERVAL = 0.1;
    // 音量→縦幅のゲイン（大きいほど声に反応して高くなる）。
    const HEIGHT_GAIN = 1.8;

    const frame = (now: number) => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      const dt =
        lastRef.current == null ? 0 : Math.min(0.1, (now - lastRef.current) / 1000);
      lastRef.current = now;

      const levels = levelsRef.current;
      const analyser = analyserRef.current;
      const data = dataRef.current;

      // 全体音量(0-1)
      let volume = 0;
      if (analyser && data) {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        volume = Math.min(1, sum / data.length / 90);
      }
      // 無音時は波を打たない（ノイズゲート）。音量が閾値未満なら 0＝平ら。
      const gated = volume < 0.06 ? 0 : volume;

      const barW = 3;
      const gap = 3;
      const step = barW + gap;
      const minBarH = 7; // 無音時でも見える最低の縦幅
      const mid = h / 2;
      const totalW = barCount * step - gap;
      const offsetX = Math.max(0, (w - totalW) / 2); // 中央寄せ

      // 時間経過に応じてバッファを左→右へスクロール。
      // 進捗が1バー分たまるごとに、最新値を左端(index0)へ差し込み、右端を捨てる。
      phaseRef.current += dt / STEP_INTERVAL;
      while (phaseRef.current >= 1) {
        phaseRef.current -= 1;
        levels.pop();
        levels.unshift(gated);
      }

      const phase = phaseRef.current;
      // index0(最新)を右端に置き、加齢とともに左へ流す（向きを反転）。
      for (let i = 0; i < levels.length; i++) {
        const level = levels[i];
        const amp = Math.min(1, level * HEIGHT_GAIN);
        const barH = Math.max(minBarH, amp * h * 0.92);
        const x = offsetX + (barCount - (i + phase)) * step;
        if (x < -step || x > w) continue;
        const y = mid - barH / 2;

        const colorMix = Math.min(1, level * 2.2);
        ctx.fillStyle = mixColor(colorMix);

        ctx.beginPath();
        ctx.roundRect(x, y, barW, barH, barW / 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    lastRef.current = null;
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [barCount]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, display: "block" }}
      aria-hidden="true"
    />
  );
};

export default VoiceWaveform;
